import { Phase, TUNING, type GameState, type LineClearEvent } from '../core/game.ts';
import type { Cell } from '../core/board.ts';
import { PIECE_COLORS, type PieceType } from '../core/piece.ts';
import type { GameSession } from '../app/session.ts';
import { collapseProgress, type GameView } from './canvas.ts';
import {
  Painter,
  drawMusicChip,
  drawSoundChip,
  drawThemeChip,
  drawVolumeChip,
  type VolumeLevel,
} from './painter.ts';
import { HitList } from './hits.ts';
import type { Surface } from './surface.ts';
import {
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  LAYOUT,
  TWIN_LAYOUT as T,
  TYPE,
  faces,
  formatCounter,
  formatScore,
  onBlock,
  palette,
} from './theme.ts';
import { drawMiniPiece } from './pieces.ts';
import { ClearImpact } from './clearImpact.ts';

const CELL = T.cell;
const FIELD_W = CELL * TUNING.board.width;
const FIELD_H = CELL * TUNING.board.visibleHeight;
const FIELD_Y = T.fieldY;
const FRAME = T.frame;
const RAIL_W = 42;
const MINI_CELL = 8;

export class TwinRenderer {
  readonly hits = new HitList();
  private readonly painter: Painter;
  private readonly clearImpacts = [new ClearImpact(), new ClearImpact()] as const;

  constructor(private readonly surface: Surface) {
    this.painter = surface.painter;
  }

  showMultiLineClear(index: 0 | 1, state: GameState, event: LineClearEvent): void {
    this.clearImpacts[index].trigger(state, event);
  }

  draw(
    session: GameSession,
    view: GameView,
    dtMs: number,
    muted = false,
    volume: VolumeLevel = 'medium',
    music = true,
  ): void {
    for (const effect of this.clearImpacts) effect.update(dtMs);
    this.surface.begin();
    this.hits.clear();

    this.drawHeader(session, muted, volume, music);
    T.boards.forEach((board, i) => {
      const state = session.games[i];
      this.drawIdentity(state, board.x, board.label, board.color);
      this.drawBoard(state, board.x, i as 0 | 1);
      this.drawRail(state, board.railX, board.rightAligned);
      this.drawSummary(state, board.x);
    });
    this.drawFooter();

    if (session.phase === Phase.Paused) this.drawPause(session);
    else if (session.phase === Phase.GameOver) this.drawGameOver(session, view);
  }

  private drawHeader(
    session: GameSession,
    muted: boolean,
    volume: VolumeLevel,
    music: boolean,
  ): void {
    const p = this.painter;
    const c = palette();
    const y = LAYOUT.pad + 8;
    p.typed('KD-TETRIS', LAYOUT.pad, y, TYPE.brand, c.textLabel);

    let right = CANVAS_WIDTH - LAYOUT.pad;
    const status = session.phase === Phase.GameOver ? 'GAME OVER' : session.phase;
    const color = session.phase === Phase.Playing ? '#92c745' : session.phase === Phase.GameOver ? '#e72f6f' : '#63c9d8';
    right -= p.headerChip(status, right, y, color).w + 8;
    right -= drawThemeChip(p, this.hits, right, y) + 8;
    right -= drawSoundChip(p, this.hits, right, y, muted) + 8;
    right -= drawMusicChip(p, this.hits, right, y, music) + 8;
    right -= drawVolumeChip(p, this.hits, right, y, volume) + 8;
    if (session.phase === Phase.Playing) {
      const box = p.headerChip('PAUSE', right, y, { f: c.panelAlt, textColor: c.textDim });
      this.hits.add('game.pause', box.x, box.y, box.w, box.h);
    }
    p.rule(LAYOUT.pad, y + 10, CANVAS_WIDTH - LAYOUT.pad * 2);

    // The combined figure is the one that goes on the board, so it carries the
    // accent; the per-board scores sit with their own stacks below.
    const bannerBaseline = T.bannerY + 8;
    p.typed(
      'TWIN · ONE INPUT MOVES BOTH · MIRRORED BAGS',
      LAYOUT.pad,
      bannerBaseline,
      TYPE.footer,
      c.textFaint,
    );
    p.typed(
      `TOTAL ${formatScore(session.score)}`,
      CANVAS_WIDTH - LAYOUT.pad,
      bannerBaseline,
      { size: 13, weight: 800, tracking: 0.04 },
      c.accent,
      'right',
    );
  }

  /**
   * A board's identity and its own score, read alongside the stack rather than
   * under it. A topped-out board fades both.
   */
  private drawIdentity(state: GameState, fieldX: number, label: string, color: string): void {
    const p = this.painter;
    const c = palette();
    const ctx = this.surface.ctx;
    const size = T.identity;

    ctx.save();
    if (state.phase === Phase.GameOver) ctx.globalAlpha = T.deadAlpha;
    p.bevelRect(fieldX, T.headerY, size, size, faces(color), 3);
    p.typed(
      label,
      fieldX + size / 2,
      T.headerY + size / 2 + 4,
      { size: 11, weight: 800, tracking: 0 },
      onBlock(color).text,
      'center',
    );
    p.typed(
      formatScore(state.score),
      fieldX + FIELD_W,
      T.headerY + 16,
      { size: 14, weight: 700, tracking: 0.04 },
      c.text,
      'right',
    );
    ctx.restore();
  }

  private drawBoard(state: GameState, fieldX: number, index: 0 | 1): void {
    const p = this.painter;
    const c = palette();
    const ctx = this.surface.ctx;
    const effect = this.clearImpacts[index];
    const shake = effect.shakeOffset();

    ctx.save();
    ctx.translate(shake.x, shake.y);
    p.bevelRect(fieldX - FRAME, FIELD_Y - FRAME, FIELD_W + FRAME * 2, FIELD_H + FRAME * 2, c.well, FRAME);
    p.fillRect(fieldX, FIELD_Y, FIELD_W, FIELD_H, c.field);

    ctx.save();
    ctx.beginPath();
    ctx.rect(fieldX, FIELD_Y, FIELD_W, FIELD_H);
    ctx.clip();
    for (let y = 0; y < TUNING.board.visibleHeight; y++) {
      for (let x = 0; x < TUNING.board.width; x++) {
        p.blockCell(fieldX + x * CELL, FIELD_Y + y * CELL, CELL, c.cellEmpty);
      }
    }

    this.drawStack(state, fieldX);
    if (!state.clearAnim) {
      for (const cell of state.ghostCells()) {
        p.blockCell(fieldX + cell.x * CELL, FIELD_Y + (cell.y - TUNING.board.bufferHeight) * CELL, CELL, c.ghost);
      }
      const type = state.active?.type;
      if (type) {
        for (const cell of state.activeCells()) {
          p.blockCell(
            fieldX + cell.x * CELL,
            FIELD_Y + (cell.y - TUNING.board.bufferHeight) * CELL,
            CELL,
            PIECE_COLORS[type],
          );
        }
      }
    }
    ctx.restore();
    effect.draw(p, fieldX, FIELD_Y, CELL);

    if (state.phase === Phase.GameOver) {
      p.fillRect(fieldX, FIELD_Y, FIELD_W, FIELD_H, c.scrim);
      p.typed('TOPPED OUT', fieldX + FIELD_W / 2, FIELD_Y + FIELD_H / 2, TYPE.overlayTitle, c.text, 'center');
    }
    ctx.restore();
  }

  private drawStack(state: GameState, fieldX: number): void {
    const cells = state.board.snapshot();
    const anim = state.clearAnim;
    const clearing = new Set(anim?.rows ?? []);
    const flashing = anim !== null && anim.elapsedMs < TUNING.lineClearFlashMs;
    const collapse = anim ? collapseProgress(anim) : 0;
    for (let y = TUNING.board.bufferHeight; y < TUNING.board.height; y++) {
      const isClearing = clearing.has(y);
      if (isClearing && !flashing) continue;
      const below = anim && !isClearing
        ? anim.rows.reduce((count, cleared) => count + (cleared > y ? 1 : 0), 0)
        : 0;
      const shift = below * CELL * collapse;
      for (let x = 0; x < TUNING.board.width; x++) {
        const cell: Cell = cells[y * TUNING.board.width + x];
        if (!cell) continue;
        this.painter.blockCell(
          fieldX + x * CELL,
          FIELD_Y + (y - TUNING.board.bufferHeight) * CELL + shift,
          CELL,
          isClearing ? '#ffffff' : PIECE_COLORS[cell],
        );
      }
    }
  }

  private drawRail(state: GameState, x: number, rightAligned: boolean): void {
    const p = this.painter;
    const c = palette();
    const alignX = rightAligned ? x + RAIL_W : x;
    const align = rightAligned ? 'right' : 'left';
    p.typed('HOLD', alignX, FIELD_Y + 8, TYPE.footer, c.textLabel, align);
    p.bevelRect(x, FIELD_Y + 16, RAIL_W, 42, c.panel, 3);
    if (state.hold) this.drawMini(state.hold, x, FIELD_Y + 16, RAIL_W, 42, state.canHold ? 1 : 0.3);

    p.typed('NEXT', alignX, FIELD_Y + 80, TYPE.footer, c.textLabel, align);
    const nextY = FIELD_Y + 88;
    p.bevelRect(x, nextY, RAIL_W, 190, c.panel, 3);
    state.preview(TUNING.previewCount).forEach((type, index) => {
      this.drawMini(type, x, nextY + index * 37, RAIL_W, 37, index === 0 ? 1 : 0.55);
    });

    if (state.backToBack) p.chip('B2B', x, nextY + 204, '#e72f6f', { size: 7, weight: 800, tracking: 0.08 }, 3, 2, 2);
    if (state.combo > 0) p.typed(`C${state.combo}`, alignX, nextY + 242, TYPE.footer, c.textDim, align);
  }

  private drawSummary(state: GameState, x: number): void {
    const p = this.painter;
    const c = palette();
    const y = T.summaryY + 8;
    p.typed(`LINES ${formatCounter(state.lines)}`, x, y, { size: 9, weight: 500, tracking: 0.16 }, c.textDim);
    p.typed(
      `LEVEL ${formatCounter(state.level)}`,
      x + FIELD_W,
      y,
      { size: 9, weight: 500, tracking: 0.16 },
      c.textDim,
      'right',
    );
  }

  private drawPause(session: GameSession): void {
    const p = this.painter;
    const c = palette();
    p.fillRect(0, LAYOUT.pad + 21, CANVAS_WIDTH, CANVAS_HEIGHT - LAYOUT.pad - 21, c.scrim);
    const cx = CANVAS_WIDTH / 2;
    const y = 254;
    p.typed('PAUSED', cx, y, TYPE.overlayTitle, c.text, 'center');
    p.typed(`TOTAL ${formatScore(session.score)} · ${session.lines} LINES`, cx, y + 30, TYPE.footer, c.textDim, 'center');
    this.overlayButton('RESUME', 'ESC', cx, y + 56, true, 'pause.resume');
    this.overlayButton('RESTART', 'R', cx, y + 106, false, 'pause.restart');
    this.overlayButton('MENU', 'M', cx, y + 156, false, 'pause.menu');
  }

  private drawGameOver(session: GameSession, view: GameView): void {
    const p = this.painter;
    const c = palette();
    p.fillRect(0, LAYOUT.pad + 21, CANVAS_WIDTH, CANVAS_HEIGHT - LAYOUT.pad - 21, c.scrim);
    const cx = CANVAS_WIDTH / 2;
    let y = 240;
    p.typed('TWIN RUN OVER', cx, y, TYPE.overlayTitle, c.text, 'center');
    y += 30;
    p.typed(view.playerName, cx, y, TYPE.footer, c.textLabel, 'center');
    y += 18;
    const score = formatScore(session.score);
    const plaqueW = p.measureTyped(score, TYPE.bigScore) + 56;
    p.bevelRect(cx - plaqueW / 2, y, plaqueW, 58, c.accentFaces, 6);
    p.typed(score, cx, y + 39, TYPE.bigScore, c.onAccent, 'center');
    y += 82;
    p.typed(`${session.lines} LINES · PEAK LEVEL ${formatCounter(session.level)}`, cx, y, TYPE.footer, c.textDim, 'center');
    y += 28;
    const rank = view.rank === null ? 'NO PLACEMENT' : `RANK ${formatCounter(view.rank)}`;
    const rankToken = TYPE.chip;
    const rankW = Math.round(p.measureTyped(rank, rankToken) + 20);
    p.chip(rank, cx - rankW / 2, y, view.rank === null ? '#5c7185' : '#92c745', rankToken, 7, 3, 3);
    this.overlayButton('RESTART', 'R', cx, y + 48, true, 'over.restart');
    this.overlayButton('SCOREBOARD', 'S', cx, y + 98, false, 'over.scoreboard');
    this.overlayButton('MENU', 'M', cx, y + 148, false, 'over.menu');
  }

  private overlayButton(label: string, hint: string, cx: number, y: number, primary: boolean, id: 'pause.resume' | 'pause.restart' | 'pause.menu' | 'over.restart' | 'over.scoreboard' | 'over.menu'): void {
    const p = this.painter;
    const c = palette();
    const w = 210;
    const h = 42;
    const color = '#92c745';
    const on = primary ? onBlock(color) : { text: c.textDim, dim: c.textFaint };
    p.blockButton(cx - w / 2, y, w, h, primary ? faces(color) : c.panelAlt, 5, on.text, on.dim, {
      label,
      labelType: { size: 12, weight: 700, tracking: 0.18 },
      hint,
      hintType: { size: 10, weight: 700, tracking: 0.1 },
    });
    this.hits.add(id, cx - w / 2, y, w, h);
  }

  private drawMini(type: PieceType, boxX: number, boxY: number, boxW: number, boxH: number, alpha: number): void {
    drawMiniPiece(this.painter, type, boxX, boxY, boxW, boxH, MINI_CELL, alpha);
  }

  private drawFooter(): void {
    const p = this.painter;
    const c = palette();
    const y = CANVAS_HEIGHT - LAYOUT.pad;
    p.rule(LAYOUT.pad, y - 22, CANVAS_WIDTH - LAYOUT.pad * 2);
    p.typed('ONE KEY MOVES BOTH · ESC PAUSE · R RESTART · F2 THEME', LAYOUT.pad, y, TYPE.footer, c.textFaint);
  }
}
